import React from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Globe, ChevronDown, Check } from "lucide-react";
import { LANGUAGES } from "@/i18n";
import { buildLangUrl } from "@/hooks/useLangSync";
import { Flag } from "./Flag";

export const LanguageSwitcher = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = LANGUAGES.find((l) => l.code === i18n.language?.split("-")[0]) || LANGUAGES[0];

  const change = (code) => {
    i18n.changeLanguage(code);
    const url = buildLangUrl(code, location.pathname) + location.search + location.hash;
    navigate(url);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        onClick={() => setOpen(!open)}
        data-testid="language-switcher-button"
        className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all border ${
          open ? "border-hx-neon/40 bg-white/[0.03]" : "border-white/[0.10] bg-white/[0.03] hover:border-white/[0.10]"
        }`}
      >
        <Globe size={14} className="text-hx-neon" />
        {!compact && <Flag code={current.code} />}
        <span className="font-mono text-xs uppercase tracking-[0.18em] text-hx-text">{current.short}</span>
        <ChevronDown size={12} className={`text-hx-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute top-full right-0 mt-2 w-56 hx-glass-strong rounded-lg overflow-hidden z-50 shadow-2xl"
            data-testid="language-switcher-menu"
          >
            <div className="p-2">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => change(l.code)}
                  data-testid={`language-option-${l.code}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all ${
                    l.code === current.code ? "bg-hx-neon/10 text-hx-neon" : "hover:bg-white/[0.03] text-hx-text"
                  }`}
                >
                  <Flag code={l.code} className="text-xl" />
                  <div className="flex-1 text-left">
                    <div className="font-display font-semibold text-sm">{l.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hx-muted">{l.short}</div>
                  </div>
                  {l.code === current.code && <Check size={14} className="text-hx-neon" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
